import type { SpreadJSEngineResult } from '@/lib/engine/spreadjs-engine-client';

import { EngineDiagnostics } from '../diagnostics/EngineDiagnostics';
import { InfoCard, InfoSection } from '../diagnostics/InfoSection';
import { EngineDiffView } from '../EngineDiffView';

interface ValidationTabProps {
  spreadjsResult: SpreadJSEngineResult | null;
  spreadjsEvaluating: boolean;
  hyperformulaResult?: any;
  sheetJson?: any;
  isValidJson: boolean;
  stages?: any;
  lastStage?: string;
  stageErrors?: any;
  error?: string;
}

export function ValidationTab({
  spreadjsResult,
  spreadjsEvaluating,
  hyperformulaResult,
  sheetJson,
  isValidJson,
  stages,
  lastStage,
  stageErrors,
  error,
}: ValidationTabProps) {
  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      {/* Engine Comparison */}
      {spreadjsEvaluating && (
        <InfoSection title="Engine Comparison">
          <InfoCard>
            <div
              style={{ padding: '1rem', textAlign: 'center', color: '#8B7764' }}
            >
              Evaluating with SpreadJS...
            </div>
          </InfoCard>
        </InfoSection>
      )}

      {spreadjsResult && hyperformulaResult && sheetJson && (
        <InfoSection title="Engine Comparison">
          <EngineDiffView
            spreadjsResult={spreadjsResult}
            hyperformulaResult={hyperformulaResult}
            sheetSpec={sheetJson}
          />
        </InfoSection>
      )}

      {/* Execution Stages */}
      {stages && (
        <InfoSection title="Execution Pipeline">
          <InfoCard>
            <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              <strong>Last Stage:</strong> {lastStage || 'unknown'}
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                display: 'grid',
                gap: '0.5rem',
                marginTop: '0.75rem',
              }}
            >
              {stages.map((stage: any, i: number) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <span>{stage.success ? '✓' : '✗'}</span>
                  <span
                    style={{ color: stage.success ? '#10b981' : '#ef4444' }}
                  >
                    {stage.stage}
                  </span>
                  {stage.error && (
                    <span style={{ color: '#991b1b', fontSize: '0.8125rem' }}>
                      ({stage.error})
                    </span>
                  )}
                </div>
              ))}
            </div>
            {stageErrors && stageErrors.length > 0 && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.5rem',
                  }}
                >
                  Failed Stages:
                </div>
                <ul
                  style={{
                    margin: '0.5rem 0',
                    paddingLeft: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#991b1b',
                  }}
                >
                  {stageErrors.map((err: any, i: number) => (
                    <li key={i}>
                      {err.stage}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </InfoCard>
        </InfoSection>
      )}

      {/* JSON Parse Status */}
      <InfoSection title="JSON Parse Status">
        <InfoCard variant={isValidJson ? 'success' : 'error'}>
          <strong>{isValidJson ? '✓ Valid JSON' : '✗ Invalid JSON'}</strong>
        </InfoCard>
      </InfoSection>

      {/* Overall Evaluation Status */}
      {spreadjsResult && !spreadjsEvaluating && (
        <InfoSection title="Overall Evaluation">
          <InfoCard
            variant={
              spreadjsResult.errorCells.length === 0 ? 'success' : 'error'
            }
          >
            <strong>
              {spreadjsResult.errorCells.length === 0
                ? '✓ No Errors'
                : '✗ Has Errors'}
            </strong>
          </InfoCard>
        </InfoSection>
      )}

      {/* Computation Errors */}
      {spreadjsResult && !spreadjsEvaluating && (
        <InfoSection title="Computation Errors">
          <InfoCard
            variant={spreadjsResult.errorCells.length > 0 ? 'error' : 'success'}
          >
            <strong>
              {spreadjsResult.errorCells.length > 0
                ? `✗ ${spreadjsResult.errorCells.length} Error${spreadjsResult.errorCells.length !== 1 ? 's' : ''}`
                : '✓ No Computation Errors'}
            </strong>
          </InfoCard>
        </InfoSection>
      )}

      {/* SpreadJS Diagnostics */}
      {spreadjsResult && (
        <EngineDiagnostics
          title="SpreadJS Diagnostics (Frontend Evaluation)"
          errors={spreadjsResult.errors}
          errorCells={spreadjsResult.errorCells}
          detailedErrors={spreadjsResult.detailedErrors}
        />
      )}

      {/* HyperFormula Diagnostics */}
      {hyperformulaResult && (
        <EngineDiagnostics
          title="HyperFormula Diagnostics (Backend Evaluation)"
          errors={hyperformulaResult.errors}
          errorCells={hyperformulaResult.errorCells}
          detailedErrors={hyperformulaResult.detailedErrors}
        />
      )}

      {/* Error Message */}
      {error && (
        <InfoSection title="Error Message">
          <div
            style={{
              padding: '1rem',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              color: '#991b1b',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        </InfoSection>
      )}
    </div>
  );
}
